import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { QRSessionProvider } from './contexts/QRSessionContext';
import './App.css';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';

// Dashboard Pages
import AdminDashboard from './pages/AdminDashboard';
import AddStaff from './pages/AddStaff';
import EditStaff from './pages/EditStaff';
import EditStudent from './pages/EditStudent';
import AddStudent from './pages/AddStudent';
import AddStudentForm from './pages/AddStudentForm';
import BulkImportStudents from './pages/BulkImportStudents';
import AddModule from './pages/AddModule';
import AddModuleHere from './pages/AddModuleHere';
import EditAddModule from './pages/EditModuleWorking';
import EditMapModule from './pages/EditMapModule';
import MapModuleToDegree from './pages/MapModuleToDegree';
import AcademicManagement from './pages/AcademicManagement';
import AddDegreeProgram from './pages/AddDegreeProgram';
import EditDegreeProgram from './pages/EditDegreeProgram';
import AddGroup from './pages/AddGroup';
import EditGroup from './pages/EditGroup';
import TimeTableManagement from './pages/TimeTableManagement';
import AddTimeTable from './pages/AddTimeTable';
import EditTimeTable from './pages/EditTimeTable';
import ReportsAnalytics from './pages/ReportsAnalytics';
import Classes from './pages/Classes';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import StudentsList from './pages/StudentsList';
import LecturersList from './pages/LecturersList';
import LecturerDashboard from './pages/LecturerDashboard';
import LecturerProfile from './pages/LecturerProfile';
import LecturerStudentsList from './pages/LecturerStudentsList';
import QRConfig from './pages/QRConfig';
import QRDisplayPage from './pages/QRDisplay';
import ManualAttendanceMark from './pages/ManualAttendanceMark';
import StudentDashboard from './pages/StudentDashboard'; // Make sure this exists
import StudentProfile from './pages/StudentProfile';
import StudentSettings from './pages/StudentSettings';
import StudentAttendanceAnalysis from './pages/StudentAttendanceAnalysis';
import LecturerSettings from './pages/LecturerSettings';
import ViewTimeTable from './pages/ViewTimeTable'; // Import ViewTimeTable component
import StudentTimeTable from './pages/StudentTimeTable'; // Import StudentTimeTable component
import AttendanceRecords from './pages/AttendanceRecords';
import PrivacyPolicy from './pages/PrivacyPolicy'; // Import PrivacyPolicy component

// Components
import ProtectedRoute from './components/ProtectedRoute';
import SplashScreen from './components/SplashScreen';

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AuthProvider>
      <QRSessionProvider>
        <Router>
          <div className="App">
            {loading && <SplashScreen />}
            <Routes>
              {/* Authentication Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              
              {/* Protected Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-staff" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddStaff />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-staff/:staffId" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditStaff />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-student" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddStudent />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-student-form" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddStudentForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-student/:id" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditStudent />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/bulk-import-students" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <BulkImportStudents />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-module" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddModule />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-module-here" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddModuleHere />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-add-module" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditAddModule />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-map-module" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditMapModule />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/map-module-to-degree" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <MapModuleToDegree />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/academic-management" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AcademicManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-degree-program" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddDegreeProgram />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-degree-program" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditDegreeProgram />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-group" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddGroup />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-group" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditGroup />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/timetable-management" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <TimeTableManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-timetable" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AddTimeTable />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/edit-timetable" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <EditTimeTable />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/reports-analytics" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <ReportsAnalytics />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/students-list"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <StudentsList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/lecturer/students"
                element={
                  <ProtectedRoute allowedRoles={['lecturer']}>
                    <LecturerStudentsList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/lecturers"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <LecturersList />
                  </ProtectedRoute>
                } 
              />
              
              {/* Lecturer Routes */}
              <Route 
                path="/lecturer-dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer']}>
                    <LecturerDashboard />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/lecturer/settings" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer']}>
                    <LecturerSettings />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/lecturer/profile" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer']}>
                    <LecturerProfile />
                  </ProtectedRoute>
                } 
              />

              {/* Student Routes */}
              <Route 
                path="/student-dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student-timetable" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentTimeTable />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student/profile" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student/settings" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentSettings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student-attendance-analysis" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentAttendanceAnalysis />
                  </ProtectedRoute>
                } 
              />
              
              {/* Common Protected Routes */}
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/qr" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer', 'admin']}>
                    <QRConfig />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/manual-attendance-mark" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer', 'admin']}>
                    <ManualAttendanceMark />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/qr-display" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer', 'admin']}>
                    <QRDisplayPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/classes" 
                element={
                  <ProtectedRoute allowedRoles={['lecturer', 'admin']}>
                    <Classes />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/view-timetable" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <ViewTimeTable />
                  </ProtectedRoute>
                } 
              />
              
              {/* Temporary routes for testing QR functionality without auth */}
              <Route path="/qr-test" element={<QRConfig />} />
              <Route path="/qr-display-test" element={<QRDisplayPage />} />
              
              {/* Redirects */}
              
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        </Router>
      </QRSessionProvider>
    </AuthProvider>
  );
}

export default App;